import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  Box,
  Divider,
  Badge,
  Tooltip,
  useTheme,
  useMediaQuery,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Settings,
  SmartToy,
  Home,
  Chat as ChatIcon,
  AccessTime,
  BugReport,
  Receipt,
  Folder,
  Group,
  Assessment,
  Description,
  RequestQuote,
  AdminPanelSettings,
  Add as AddIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { ChatSidebar } from './ChatSidebar';
import { WorkspaceSwitcher } from './WorkspaceSwitcher';
import { apiService } from '../services/api';
import { personalizationService, PersonalizationSettings } from '../services/personalizationService';
import { PermissionsService, PERMISSIONS } from '../services/permissionsService';

interface LogoComponentProps {
  logoUrl?: string;
  companyName: string;
  borderRadius: string;
}

const LogoComponent: React.FC<LogoComponentProps> = ({ logoUrl, companyName, borderRadius }) => {
  const [logoError, setLogoError] = useState(false);
  const [logoLoading, setLogoLoading] = useState(true);

  const handleLogoError = () => {
    setLogoError(true);
    setLogoLoading(false);
  };

  const handleLogoLoad = () => {
    setLogoError(false);
    setLogoLoading(false);
  };

  // Reset error state when logoUrl changes
  useEffect(() => {
    if (logoUrl) {
      setLogoError(false);
      setLogoLoading(true);
    }
  }, [logoUrl]);

  // If no logo URL or logo failed to load, show company initial
  if (!logoUrl || logoError) {
    return (
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: parseInt(borderRadius.replace('px', '')) / 2,
          backgroundColor: 'rgba(255,255,255,0.2)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mr: 1.5,
          fontSize: '16px',
          fontWeight: 'bold',
          color: 'white',
        }}
      >
        {companyName.charAt(0).toUpperCase()}
      </Box>
    );
  }

  return (
    <Box sx={{ mr: 1.5, display: 'flex', alignItems: 'center' }}>
      {logoLoading && (
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: parseInt(borderRadius.replace('px', '')) / 2,
            backgroundColor: 'rgba(255,255,255,0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '12px',
            color: 'white',
          }}
        >
          ...
        </Box>
      )}
      <img 
        src={logoUrl} 
        alt={`${companyName} logo`}
        onError={handleLogoError}
        onLoad={handleLogoLoad}
        style={{ 
          height: '40px', 
          width: '40px',
          objectFit: 'contain',
          borderRadius: parseInt(borderRadius.replace('px', '')) / 2,
          display: logoLoading ? 'none' : 'block',
        }}
      />
    </Box>
  );
};

interface ModernNavigationProps {
  activeTab: string;
  onTabChange: (tab: string) => void;
}

export const ModernNavigation: React.FC<ModernNavigationProps> = ({
  activeTab,
  onTabChange,
}) => {
  const { state: authState, logout, refreshProfile } = useAuth();
  const [aiMenuAnchor, setAiMenuAnchor] = useState<null | HTMLElement>(null);
  const [settingsMenuAnchor, setSettingsMenuAnchor] = useState<null | HTMLElement>(null);
  const [chatOpen, setChatOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [channelCount, setChannelCount] = useState(0);
  const [directCount, setDirectCount] = useState(0);
  const [personalizationSettings, setPersonalizationSettings] = useState<PersonalizationSettings | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  
  // Create Workspace dialog state
  const [createWorkspaceOpen, setCreateWorkspaceOpen] = useState(false);
  const [newWorkspaceName, setNewWorkspaceName] = useState('');
  const [newWorkspaceIndustry, setNewWorkspaceIndustry] = useState('');
  const [creatingWorkspace, setCreatingWorkspace] = useState(false);
  
  // 🧠 CRITICAL FIX: Use MUI theme (not state) for AppBar colors
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  // Load personalization settings and refresh profile
  useEffect(() => {
    const loadPersonalization = async () => {
      try {
        const settings = await personalizationService.getPersonalizationSettings();
        setPersonalizationSettings(settings);
        console.log('🎨 Navigation: Initial personalization loaded:', settings);
      } catch (error) {
        console.error('Failed to load personalization settings:', error);
      }
    };

    // Refresh user profile to get latest permissions
    const refreshUserProfile = async () => {
      try {
        await refreshProfile();
      } catch (error) {
        console.error('Failed to refresh user profile:', error);
      }
    };

    // Only load once on mount
    loadPersonalization();
    refreshUserProfile();
  }, []); // Remove refreshProfile dependency to prevent loops

  // Separate useEffect for event listening
  useEffect(() => {
    // Listen for personalization updates
    const handlePersonalizationUpdate = (event: CustomEvent<PersonalizationSettings>) => {
      console.log('🎨 Navigation: Personalization updated event received:', event.detail);
      setPersonalizationSettings(event.detail);
    };

    window.addEventListener('personalization-updated' as any, handlePersonalizationUpdate);
    
    return () => {
      window.removeEventListener('personalization-updated' as any, handlePersonalizationUpdate);
    };
  }, []); // Empty dependency array - only set up once

  // Poll for unread messages when chat is closed
  useEffect(() => {
    if (!chatOpen) {
      const fetchUnreadCount = async () => {
        try {
          const data = await apiService.getUnreadMessageCount();
          setUnreadCount(data.count || 0);
          setChannelCount(data.channelCount || 0);
          setDirectCount(data.directCount || 0);
        } catch (error) {
          console.error('Error fetching unread count:', error);
        }
      };

      fetchUnreadCount();
      const interval = setInterval(fetchUnreadCount, 30000); // Check every 30 seconds

      return () => clearInterval(interval);
    }
  }, [chatOpen]);

  // Listen for messages-read events to update unread count immediately
  useEffect(() => {
    const handleMessagesRead = async () => {
      try {
        const data = await apiService.getUnreadMessageCount();
        setUnreadCount(data.count || 0);
      } catch (error) {
        console.error('Error fetching unread count after messages read:', error);
      }
    };

    window.addEventListener('messages-read', handleMessagesRead);
    return () => window.removeEventListener('messages-read', handleMessagesRead);
  }, []);

  const handleChatOpen = () => {
    setChatOpen(true);
    setUnreadCount(0); // Reset count when opening chat
  };

  // Close chat when navigating to other tabs
  useEffect(() => {
    setChatOpen(false);
  }, [activeTab]);

  const handleLogout = () => {
    logout();
    handleSettingsMenuClose();
  };

  const handleAiMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAiMenuAnchor(event.currentTarget);
  };

  const handleAiMenuClose = () => {
    setAiMenuAnchor(null);
  };

  const handleAiSettings = () => {
    onTabChange('ai-settings');
    handleAiMenuClose();
  };

  const handleScreenshotHistory = () => {
    onTabChange('screenshot-history');
    handleAiMenuClose();
  };

  const handleAiAnalytics = () => {
    onTabChange('ai-analytics');
    handleAiMenuClose();
  };

  const handleSettingsMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setSettingsMenuAnchor(event.currentTarget);
  };

  const handleSettingsMenuClose = () => {
    setSettingsMenuAnchor(null);
  };

  const handleProfileSettings = () => {
    onTabChange('profile-settings');
    handleSettingsMenuClose();
  };

  const handleAccountSettings = () => {
    onTabChange('account-settings');
    handleSettingsMenuClose();
  };

  const handlePersonalizationSettings = () => {
    onTabChange('personalization-settings');
    handleSettingsMenuClose();
  };

  // Create Workspace handlers
  const handleOpenCreateWorkspace = () => {
    handleSettingsMenuClose();
    setCreateWorkspaceOpen(true);
  };

  const handleCloseCreateWorkspace = () => {
    setCreateWorkspaceOpen(false);
    setNewWorkspaceName('');
    setNewWorkspaceIndustry('');
  };

  const handleCreateWorkspace = async () => {
    if (!newWorkspaceName.trim()) return;

    try {
      setCreatingWorkspace(true);
      await apiService.createWorkspace({
        name: newWorkspaceName.trim(),
        industry: newWorkspaceIndustry || undefined,
      });

      // Clear form and close dialog
      handleCloseCreateWorkspace();

      // Reload to switch to new workspace
      window.location.reload();
    } catch (error) {
      console.error('Failed to create workspace:', error);
      alert('Kunde inte skapa workspace. Försök igen.');
    } finally {
      setCreatingWorkspace(false);
    }
  };

  // Filter navigation items based on user permissions
  // NOTE: Expenses, Invoices, Estimates temporarily hidden from nav (pages still exist)
  const allNavigationItems = [
    { key: 'ai-analytics', label: 'Home', icon: <Home />, permission: null }, // Build Home into the permissions check logic below if needed, usually open to all
    { key: 'time', label: 'Time', icon: <AccessTime />, permission: PERMISSIONS.VIEW_TIME },
    { key: 'projects', label: 'Projects', icon: <Folder />, permission: PERMISSIONS.VIEW_PROJECTS },
    { key: 'team', label: 'Team', icon: <Group />, permission: PERMISSIONS.VIEW_TEAM },
    { key: 'reports', label: 'Reports', icon: <Assessment />, permission: PERMISSIONS.VIEW_REPORTS },
    { key: 'testing', label: 'Testing', icon: <BugReport />, permission: PERMISSIONS.VIEW_TESTING },
  ];

  const userPermissions = authState.user?.permissions || [];
  
  const navigationItems = allNavigationItems.filter(item => 
    // If permission is null, always show (like Home)
    item.permission === null || PermissionsService.hasPermission(userPermissions, item.permission)
  );

  // Get dynamic data from personalization settings
  const companyName = personalizationSettings?.branding.companyName || 'Time Tracker';
  const logoUrl = personalizationSettings?.branding.logo;
  const showLogo = personalizationSettings?.branding.showLogo !== false;
  const borderRadius = personalizationSettings?.theme.borderRadius || '8px';

  const drawer = (
    <Box>
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {showLogo && (
          <LogoComponent 
            logoUrl={logoUrl}
            companyName={companyName}
            borderRadius={borderRadius}
          />
        )}
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {companyName}
        </Typography>
      </Box>
      <Divider />
      <List>
        {navigationItems.map((item) => (
          <ListItem key={item.key} disablePadding>
            <ListItemButton
              selected={activeTab === item.key}
              onClick={() => {
                onTabChange(item.key);
                handleDrawerToggle();
              }}
              sx={{
                '&.Mui-selected': {
                  backgroundColor: `${theme.palette.primary.main}20`, // 20% opacity
                  color: theme.palette.primary.main,
                  '& .MuiListItemIcon-root': {
                    color: theme.palette.primary.main,
                  },
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText primary={item.label} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );

  // 🎨 Dynamic primary color from personalization state (for real-time updates)
  const dynamicPrimaryColor = personalizationSettings?.theme.primaryColor || theme.palette.primary.main;

  return (
    <AppBar 
      position="sticky"
      elevation={0}
      className="personalized-nav"
      sx={{ 
        backgroundColor: dynamicPrimaryColor, // ✅ Use state for real-time updates
        color: 'white', // ✅ White text for dark background
        borderBottom: '1px solid rgba(0,0,0,0.1)',
        transition: 'background-color 0.3s ease',
        borderRadius: borderRadius,
      }}
    >
      <Toolbar sx={{ minHeight: '64px !important', px: { xs: 2, md: 3 } }}>
        {/* Mobile Menu Icon */}
        {isMobile && (
          <IconButton
            color="inherit"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>
        )}

        {/* Workspace Switcher (replaces logo + company name) */}
        <Box sx={{ display: 'flex', alignItems: 'center', mr: 3, flexGrow: isMobile ? 1 : 0 }}>
          <WorkspaceSwitcher 
            companyName={companyName}
            logoUrl={logoUrl}
            showLogo={showLogo}
            borderRadius={borderRadius}
          />
        </Box>

        {/* Navigation Items - Desktop Only */}
        {!isMobile && (
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            {navigationItems.map((item) => (
              <Button
                key={item.key}
                color="inherit"
                onClick={() => onTabChange(item.key)}
                startIcon={item.icon}
                className="personalized-nav-item"
                sx={{
                  mr: 0.5,
                  px: 2,
                  py: 1,
                  fontSize: '14px',
                  fontWeight: activeTab === item.key ? 600 : 400,
                  textTransform: 'none',
                  borderRadius: 0,
                  borderBottom: activeTab === item.key ? '3px solid rgba(255,255,255,0.8)' : '3px solid transparent',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.1)',
                    borderBottom: '3px solid rgba(255,255,255,0.5)',
                  },
                }}
              >
                {item.label}
              </Button>
            ))}
          </Box>
        )}

        {/* Right Side Actions */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {/* Chat Button with Tooltip */}
          <Tooltip
            title={
              unreadCount === 0 
                ? "Inga nya meddelanden"
                : directCount > 0 && channelCount > 0
                  ? `${directCount} direktmeddelanden, ${channelCount} kanalmeddelanden`
                  : directCount > 0
                    ? `${directCount} nya direktmeddelanden`
                    : `${channelCount} nya kanalmeddelanden`
            }
            arrow
            placement="bottom"
          >
            <IconButton
              color="inherit"
              onClick={handleChatOpen}
              sx={{
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.1)',
                },
              }}
            >
              <Badge
                badgeContent={unreadCount > 0 ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                    {/* Show D for Direct messages or C for Channel messages */}
                    <Typography sx={{ fontSize: '0.6rem', fontWeight: 'bold' }}>
                      {directCount > 0 && channelCount > 0 
                        ? `${directCount}D+${channelCount}C`
                        : directCount > 0 
                          ? `${directCount}D`
                          : `${channelCount}C`
                      }
                    </Typography>
                  </Box>
                ) : 0}
                color="error"
                sx={{
                  '& .MuiBadge-badge': {
                    backgroundColor: directCount > 0 ? '#9c27b0' : '#ff4444', // Purple if direct messages, red otherwise
                    color: 'white',
                    fontSize: '0.6rem',
                    minWidth: directCount > 0 && channelCount > 0 ? '28px' : '16px',
                    height: '16px',
                    fontWeight: 'bold',
                    borderRadius: '8px',
                    padding: '0 4px',
                  },
                }}
              >
                <ChatIcon />
              </Badge>
            </IconButton>
          </Tooltip>

          {/* AI Button */}
          <IconButton
            color="inherit"
            onClick={handleAiMenuOpen}
            sx={{
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.1)',
              },
            }}
          >
            <SmartToy />
          </IconButton>

          {/* Settings */}
          <IconButton
            color="inherit"
            onClick={handleSettingsMenuOpen}
            sx={{
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.1)',
              },
            }}
          >
            <Settings />
          </IconButton>

          {/* User Info */}
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Avatar
              src={authState.user?.avatar || undefined}
              sx={{
                width: 32,
                height: 32,
                backgroundColor: 'rgba(255,255,255,0.2)',
                fontSize: '14px',
              }}
              onError={(e) => {
                console.error('Navigation avatar failed to load:', authState.user?.avatar);
                console.error('Error event:', e);
              }}
            >
              {!authState.user?.avatar && (authState.user?.name?.charAt(0).toUpperCase() || 'U')}
            </Avatar>
            <Typography
              variant="body2"
              sx={{
                ml: 1,
                fontSize: '14px',
                fontWeight: 500,
              }}
            >
              {authState.user?.name || 'User'}
            </Typography>
          </Box>

          {/* AI Menu */}
          <Menu
            anchorEl={aiMenuAnchor}
            open={Boolean(aiMenuAnchor)}
            onClose={handleAiMenuClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            PaperProps={{
              sx: {
                mt: 1,
                minWidth: 200,
                boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
              },
            }}
          >
            {PermissionsService.hasPermission(userPermissions, PERMISSIONS.VIEW_AI_SETTINGS) && (
              <MenuItem onClick={handleAiSettings} sx={{ fontSize: '14px' }}>
                AI Settings
              </MenuItem>
            )}
            {PermissionsService.hasPermission(userPermissions, PERMISSIONS.VIEW_SCREENSHOT_HISTORY) && (
              <MenuItem onClick={handleScreenshotHistory} sx={{ fontSize: '14px' }}>
                Screenshot History
              </MenuItem>
            )}
            {PermissionsService.hasPermission(userPermissions, PERMISSIONS.VIEW_AI_ANALYTICS) && (
              <MenuItem onClick={handleAiAnalytics} sx={{ fontSize: '14px' }}>
                AI Analytics
              </MenuItem>
            )}
          </Menu>

          {/* Settings Menu */}
          <Menu
            anchorEl={settingsMenuAnchor}
            open={Boolean(settingsMenuAnchor)}
            onClose={handleSettingsMenuClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            PaperProps={{
              sx: {
                mt: 1,
                minWidth: 180,
                boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
              },
            }}
          >
            <MenuItem onClick={handleProfileSettings} sx={{ fontSize: '14px' }}>
              Profile Settings
            </MenuItem>
            <MenuItem onClick={handleAccountSettings} sx={{ fontSize: '14px' }}>
              Account Settings
            </MenuItem>
            <MenuItem onClick={handlePersonalizationSettings} sx={{ fontSize: '14px' }}>
              Personalization
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleOpenCreateWorkspace} sx={{ fontSize: '14px', color: '#4caf50' }}>
              <AddIcon sx={{ fontSize: 18, mr: 1 }} />
              Create Workspace
            </MenuItem>
            {PermissionsService.hasPermission(userPermissions, PERMISSIONS.MANAGE_SETTINGS) && (
              <Divider />
            )}
            {PermissionsService.hasPermission(userPermissions, PERMISSIONS.MANAGE_SETTINGS) && (
              <MenuItem onClick={() => { onTabChange('admin-settings'); handleSettingsMenuClose(); }} sx={{ fontSize: '14px', color: '#1976d2' }}>
                Admin Settings
              </MenuItem>
            )}
            {(authState.user?.role === 'super_owner') && (
              <MenuItem onClick={() => { onTabChange('superadmin'); handleSettingsMenuClose(); }} sx={{ fontSize: '14px', color: '#d32f2f', fontWeight: 600 }}>
                🧠 Superadmin Dashboard
              </MenuItem>
            )}
            <Divider />
            <MenuItem onClick={handleLogout} sx={{ fontSize: '14px', color: '#d32f2f' }}>
              Sign Out
            </MenuItem>
          </Menu>

        </Box>
      </Toolbar>

      {/* Chat Sidebar */}
      <ChatSidebar
        open={chatOpen}
        onClose={() => setChatOpen(false)}
      />

      {/* Mobile Navigation Drawer */}
      <Drawer
        variant="temporary"
        anchor="left"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 240 },
        }}
      >
        {drawer}
      </Drawer>

      {/* Create Workspace Dialog */}
      <Dialog
        open={createWorkspaceOpen}
        onClose={handleCloseCreateWorkspace}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create New Workspace</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Workspace Name"
              value={newWorkspaceName}
              onChange={(e) => setNewWorkspaceName(e.target.value)}
              fullWidth
              autoFocus
              placeholder="e.g. My Company"
            />
            <FormControl fullWidth>
              <InputLabel>Industry (optional)</InputLabel>
              <Select
                value={newWorkspaceIndustry}
                label="Industry (optional)"
                onChange={(e) => setNewWorkspaceIndustry(e.target.value)}
              >
                <MenuItem value="">None</MenuItem>
                <MenuItem value="technology">Technology</MenuItem>
                <MenuItem value="marketing">Marketing & Agency</MenuItem>
                <MenuItem value="consulting">Consulting</MenuItem>
                <MenuItem value="design">Design & Creative</MenuItem>
                <MenuItem value="finance">Finance & Accounting</MenuItem>
                <MenuItem value="healthcare">Healthcare</MenuItem>
                <MenuItem value="education">Education</MenuItem>
                <MenuItem value="ecommerce">E-commerce</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCreateWorkspace} disabled={creatingWorkspace}>
            Cancel
          </Button>
          <Button
            onClick={handleCreateWorkspace}
            variant="contained"
            disabled={!newWorkspaceName.trim() || creatingWorkspace}
          >
            {creatingWorkspace ? 'Creating...' : 'Create Workspace'}
          </Button>
        </DialogActions>
      </Dialog>
    </AppBar>
  );
};
